// ローカルストレージユーティリティ

import { CalculationHistory, CalculationInput, CalculationResult } from '../types';

const STORAGE_KEY = 'cutting-calculator-history';
const MAX_HISTORY_SIZE = 50;

/**
 * 履歴を取得
 */
export function getCalculationHistory(): CalculationHistory[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    
    // Date型に変換
    return parsed.map(item => ({
      ...item,
      timestamp: new Date(item.timestamp),
    }));
  } catch (error) {
    console.warn('Failed to parse calculation history from localStorage:', error);
    return [];
  }
}

/**
 * 履歴に追加
 */
export function addCalculationHistory(
  input: CalculationInput,
  result: CalculationResult
): void {
  try {
    const history = getCalculationHistory();
    
    const newItem: CalculationHistory = {
      id: generateId(),
      timestamp: new Date(),
      input,
      result,
    };
    
    // 新しいアイテムを先頭に追加
    history.unshift(newItem);
    
    // 最大サイズを超えた場合は古いものを削除
    if (history.length > MAX_HISTORY_SIZE) {
      history.splice(MAX_HISTORY_SIZE);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch (error) {
    console.warn('Failed to save calculation history to localStorage:', error);
  }
}

/**
 * 履歴をクリア
 */
export function clearCalculationHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear calculation history from localStorage:', error);
  }
}

/**
 * 特定の履歴アイテムを削除
 */
export function removeCalculationHistory(id: string): void {
  try {
    const history = getCalculationHistory();
    const filtered = history.filter(item => item.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.warn('Failed to remove calculation history item from localStorage:', error);
  }
}

/**
 * ユニークIDを生成
 */
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

/**
 * CSVダウンロード用のファイル名を生成
 */
export function generateFilename(prefix: string = 'cutting_results'): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  
  return `${prefix}_${year}${month}${day}_${hours}${minutes}${seconds}`;
}

/**
 * Blobをダウンロード
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  
  // ブラウザの互換性のためにbodyに追加
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // メモリリークを防ぐためにURLを削除
  URL.revokeObjectURL(url);
}

/**
 * SVGをPNGとしてダウンロード
 */
export function downloadSvgAsPng(svgContent: string, filename: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        
        // 白い背景を描画
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // SVGを描画
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob((blob) => {
          if (blob) {
            downloadBlob(blob, filename);
            resolve();
          } else {
            reject(new Error('Failed to create PNG blob'));
          }
        }, 'image/png');
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load SVG image'));
      };
      
      // SVGをData URLに変換
      const svgBlob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      img.src = url;
    } catch (error) {
      reject(error);
    }
  });
}